import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { CATEGORIES, PRIORITIES, emptyItem } from "./constants";

/**
 * Multi-item correction draft form used by Testing (Return to Production)
 * and Agent Review (Request Change). The parent owns the draft state so
 * the same form works for both submission endpoints.
 */
export default function CorrectionForm({
  mode, draftItems, setDraftItems, reason, setReason,
  busy, onCancel, onSubmit,
}) {
  const patch = (idx, key, value) => {
    const nx = [...draftItems];
    nx[idx] = { ...nx[idx], [key]: value };
    setDraftItems(nx);
  };
  const removeAt = (idx) => setDraftItems(draftItems.filter((_, i) => i !== idx));
  const addItem = () => setDraftItems([...draftItems, emptyItem()]);

  return (
    <Dialog open={!!mode} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="return-corrections-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "return_prod" ? "Return to Production" : "Request Change"}</DialogTitle>
          <DialogDescription className="text-xs text-[#6C757D]">
            Document what needs to be corrected. Production cannot resubmit until every item is marked Corrected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {draftItems.map((it, idx) => (
            <div key={`draft-${idx}`} className="border border-[#E9ECEF] rounded-sm p-3 space-y-2 bg-[#F8F9FA]">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#0B132B]">Correction #{idx + 1}</div>
                {draftItems.length > 1 && (
                  <button type="button" onClick={() => removeAt(idx)} className="text-xs text-red-600 hover:underline">Remove</button>
                )}
              </div>
              <Input placeholder="Issue title (e.g. GAP rate structure incorrect)"
                     value={it.issue_title}
                     onChange={(e) => patch(idx, "issue_title", e.target.value)}
                     className="h-9 rounded-sm bg-white" data-testid={`draft-title-${idx}`} />
              <Textarea placeholder="What specifically is wrong? What needs to change?"
                        rows={2} value={it.description}
                        onChange={(e) => patch(idx, "description", e.target.value)}
                        className="rounded-sm bg-white" data-testid={`draft-desc-${idx}`} />
              <div className="grid grid-cols-2 gap-2">
                <Select value={it.category} onValueChange={(v) => patch(idx, "category", v)}>
                  <SelectTrigger className="h-9 rounded-sm bg-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={it.priority} onValueChange={(v) => patch(idx, "priority", v)}>
                  <SelectTrigger className="h-9 rounded-sm bg-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addItem}
                  className="rounded-sm text-xs" data-testid="btn-add-correction">
            <Plus size={12} className="mr-1" /> Add another correction
          </Button>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Overall reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="Why is this being returned?" className="mt-2 h-9 rounded-sm"
                   data-testid="draft-overall-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}
                  className="bg-[#0B132B] hover:bg-[#1C2541] text-white"
                  data-testid="btn-submit-corrections">
            {mode === "return_prod" ? "Return to Production" : "Request Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
